import {Card, Spin, Typography} from "antd";
import {useEffect, useState} from "react";
import ResultCellHighlight from "./Table/ResultCellHighlight";
import Plural from "./Plural";
import {useProject} from "../Context/ProjectContext";
import EmptyLine from "./EmptyLine";
import ResultText from "./Output/ResultText";
import _ from 'lodash'
import ResultHeader from "./Table/ResultHeader";
import ResultRow from "./Table/ResultRow";
import ResultHeaderCell from "./Table/ResultHeaderCell";
import ResultBody from "./Table/ResultBody";
import ResultBodyCell from "./Table/ResultBodyCell";
import ResultTable from "./Table/ResultTable";

export default function CompareSummaryPanel({preparing}) {
    const report = window.location.pathname.includes('/app/report')
    const fontFamily = report ? 'Calibri' : 'Segoe UI'
    const fontSize = report ? '11pt' : '9pt'

    const {projectHistory, filter, summaries, comparisonBaseId, comparisonCmpId, selectedReportId, updateSummary} = useProject()
    const [panel, setPanel] = useState({
        summary: {},
        wsel: {},
        planTitle: {},
        topics: [],
        filter: {}
    })

    const getId = () => {
        if(report)
            return selectedReportId
        else
            return `${comparisonBaseId}-${comparisonCmpId}`
    }

    useEffect(() => {
        const history = projectHistory.find(x => x.id === getId())
        const summary = summaries.find(x => x.id === getId())
        const _panel = {
            planTitle: {},
            summary: {},
            wsel: {},
            filter: {}
        }

        if(summary)
        {
            _panel.summary = {
                ...summary.summary,
                id: summary.id
            };
        }

        if(history) {
            _panel.wsel = history.wsel
            _panel.planTitle = {
                base: history?.base?.plan?.type || 'Base',
                compare: history?.compare?.plan?.type || 'Comparison',
            }
            _panel.filter = history.filter || {}
        }

        setPanel(_panel)
    }, [projectHistory, filter?.comparison, filter?.check, summaries, updateSummary, comparisonBaseId, comparisonCmpId, selectedReportId])

    if(report) {
        return (
            <>
                {getDetail({summary: panel.summary, fontFamily, fontSize, planTitle: panel.planTitle})}
                {renderCalculationTolTable({summary: panel.summary, fontFamily, fontSize, planTitle: panel.planTitle})}
                {renderWSEL(panel.wsel, panel.filter, fontFamily, fontSize)}
            </>
        )
    }

    const hasSummary = () => {
        return (panel?.summary?.base || panel?.summary?.compare)
    }

    return (
        <>
            <Card title={'Plan Summaries'} style={{height: '100%'}}>
                {
                    preparing && (
                        <>
                            Preparing for Comparison... {<Spin />}
                        </>
                    )
                }
                {
                    !preparing && !hasSummary() && (
                        <div style={{width: 200, display: 'flex', alignItems:'center', gap: 8}}>
                            <img src={'/info.png'} width={36} height={36} alt={''}/>
                            <div>
                                Please Run
                            </div>
                        </div>
                    )
                }
                {!preparing && hasSummary() && getDetail({summary: panel.summary, fontFamily, fontSize, planTitle: panel.planTitle})}
                <EmptyLine />
                {!preparing && hasSummary() && renderCalculationTolTable({summary: panel.summary, fontFamily, fontSize, planTitle: panel.planTitle})}
                <EmptyLine height={16}/>
                {!preparing && hasSummary() && renderWSEL(panel.wsel, panel.filter, fontFamily, fontSize)}
            </Card>
        </>
    )
}

const renderCalculationTolTable = ({summary, fontFamily, fontSize, planTitle}) => {

    return (
        <ResultTable>
            <ResultHeader>
                <ResultRow>
                    <ResultHeaderCell style={{textAlign: 'left'}}>Calculation Tolerances</ResultHeaderCell>
                    <ResultHeaderCell style={{width: 200, textAlign: 'right'}}>{planTitle?.base || 'Base Plan'}</ResultHeaderCell>
                    <ResultHeaderCell style={{width: 200, textAlign: 'right'}}>{planTitle?.compare || 'Comparison Plan'}</ResultHeaderCell>
                </ResultRow>
            </ResultHeader>
            <ResultBody>
                <ResultRow>
                    <ResultHeaderCell style={{textAlign: 'left'}}>Water surface calculation tol.</ResultHeaderCell>
                    <ResultBodyCell style={{textAlign: 'right'}}>{summary?.base?.calculation_tolerances?.StdStepTol}</ResultBodyCell>
                    <ResultBodyCell style={{textAlign: 'right'}}>{ResultCellHighlight(summary?.base?.calculation_tolerances?.StdStepTol, summary?.compare?.calculation_tolerances?.StdStepTol)}</ResultBodyCell>
                </ResultRow>
                <ResultRow>
                    <ResultHeaderCell style={{textAlign: 'left'}}>Critical depth calculation tol.</ResultHeaderCell>
                    <ResultBodyCell style={{textAlign: 'right'}}>{summary?.base?.calculation_tolerances?.CriticalTol}</ResultBodyCell>
                    <ResultBodyCell style={{textAlign: 'right'}}>{ResultCellHighlight(summary?.base?.calculation_tolerances?.CriticalTol, summary?.compare?.calculation_tolerances?.CriticalTol)}</ResultBodyCell>
                </ResultRow>
                <ResultRow>
                    <ResultHeaderCell style={{textAlign: 'left'}}>Maximum number of iterations</ResultHeaderCell>
                    <ResultBodyCell style={{textAlign: 'right'}}>{summary?.base?.calculation_tolerances?.NumOfStdStepTrials}</ResultBodyCell>
                    <ResultBodyCell style={{textAlign: 'right'}}>{ResultCellHighlight(summary?.base?.calculation_tolerances?.NumOfStdStepTrials, summary?.compare?.calculation_tolerances?.NumOfStdStepTrials)}</ResultBodyCell>
                </ResultRow>
                <ResultRow>
                    <ResultHeaderCell style={{textAlign: 'left'}}>Maximum difference tolerance</ResultHeaderCell>
                    <ResultBodyCell style={{textAlign: 'right'}}>{summary?.base?.calculation_tolerances?.MaxErrorTol}</ResultBodyCell>
                    <ResultBodyCell style={{textAlign: 'right'}}>{ResultCellHighlight(summary?.base?.calculation_tolerances?.MaxErrorTol, summary?.compare?.calculation_tolerances?.MaxErrorTol)}</ResultBodyCell>
                </ResultRow>
                <ResultRow>
                    <ResultHeaderCell style={{textAlign: 'left'}}>Flow tolerance factor</ResultHeaderCell>
                    <ResultBodyCell style={{textAlign: 'right'}}>{summary?.base?.calculation_tolerances?.FlowTolRatio}</ResultBodyCell>
                    <ResultBodyCell style={{textAlign: 'right'}}>{ResultCellHighlight(summary?.base?.calculation_tolerances?.FlowTolRatio, summary?.compare?.calculation_tolerances?.FlowTolRatio)}</ResultBodyCell>
                </ResultRow>
                <ResultRow>
                    <ResultHeaderCell style={{textAlign: 'left'}}>Maximum iteration in Split flow</ResultHeaderCell>
                    <ResultBodyCell style={{textAlign: 'right'}}>{summary?.base?.calculation_tolerances?.SplitFlowNTrial}</ResultBodyCell>
                    <ResultBodyCell style={{textAlign: 'right'}}>{ResultCellHighlight(summary?.base?.calculation_tolerances?.SplitFlowNTrial, summary?.compare?.calculation_tolerances?.SplitFlowNTrial)}</ResultBodyCell>
                </ResultRow>
                <ResultRow>
                    <ResultHeaderCell style={{textAlign: 'left'}}>Flow tolerance factor in weir split flow</ResultHeaderCell>
                    <ResultBodyCell style={{textAlign: 'right'}}>{summary?.base?.calculation_tolerances?.SplitFlowRatio}</ResultBodyCell>
                    <ResultBodyCell style={{textAlign: 'right'}}>{ResultCellHighlight(summary?.base?.calculation_tolerances?.SplitFlowRatio, summary?.compare?.calculation_tolerances?.SplitFlowRatio)}</ResultBodyCell>
                </ResultRow>
                <ResultRow>
                    <ResultHeaderCell style={{textAlign: 'left'}}>Max difference in junction split flow</ResultHeaderCell>
                    <ResultBodyCell style={{textAlign: 'right'}}>{summary?.base?.calculation_tolerances?.SplitFlowTol}</ResultBodyCell>
                    <ResultBodyCell style={{textAlign: 'right'}}>{ResultCellHighlight(summary?.base?.calculation_tolerances?.SplitFlowTol, summary?.compare?.calculation_tolerances?.SplitFlowTol)}</ResultBodyCell>
                </ResultRow>
            </ResultBody>
        </ResultTable>
    )
}

const getDetail = ({summary, fontFamily, fontSize, planTitle}) => {

    return (
        <div>
            <table style={{fontFamily: fontFamily, fontSize: fontSize}}>
                <tr>
                    <td style={{verticalAlign: 'top'}}>
                        {
                            summary.base && (
                                <div>
                                    <Typography.Title level={2} style={{fontSize: '11pt', fontFamily: fontFamily}}>
                                        {planTitle?.base || 'Base' }{' '}Plan
                                    </Typography.Title>
                                    {summary.base?.length} feet ({(summary.base?.length/5280).toFixed(2)} miles) reach
                                    <Plural
                                        count={summary.base?.crossSections}
                                        singular={'cross section'}
                                        plural={'cross sections'}
                                        render={(count, text) => (
                                            <>
                                                <br />
                                                {count} {text}
                                            </>
                                        )}
                                    />
                                    <Plural
                                        count={summary.base.single_bridge}
                                        singular={'(single opening) bridge'}
                                        plural={'(single opening) bridges'}
                                        render={(count, text) => {
                                            return (
                                                <>
                                                    <br />
                                                    {count} {text}
                                                </>
                                            )
                                        }}
                                    />
                                    <Plural
                                        count={summary.base.multiple_bridge}
                                        singular={'(multiple opening) bridge'}
                                        plural={'(multiple opening) bridges'}
                                        render={(count, text) => {
                                            return (
                                                <>
                                                    <br />
                                                    {count} {text}
                                                </>
                                            )
                                        }}
                                    />
                                    <Plural
                                        count={summary.base?.culverts}
                                        singular={'culvert'}
                                        plural={'culverts'}
                                        render={(count, text) => {
                                            return (
                                                <>
                                                    <br />
                                                    {count} {text}
                                                </>
                                            )
                                        }}
                                    />
                                    <Plural
                                        count={summary.base?.inline}
                                        singular={'inline structure'}
                                        plural={'inline structures'}
                                        render={(count, text) => {
                                            return (
                                                <>
                                                    <br/>
                                                    {count} {text}
                                                </>
                                            )
                                        }}
                                    />
                                    <Plural
                                        count={summary.base?.profiles}
                                        singular={'profile'}
                                        plural={'profiles'}
                                        render={(count, text) => (
                                            <>
                                                <br />
                                                {count} {text}
                                                <Plural
                                                    count={summary.base?.flowLocations}
                                                    singular={'location'}
                                                    plural={'locations'}
                                                    render={(count, text) => {
                                                        return ` with ${count} flow change ${text}`
                                                    }}
                                                />
                                            </>
                                        )}
                                    />
                                </div>
                            )
                        }
                    </td>
                    <td>
                        <div style={{width: 32}}></div>
                    </td>
                    <td style={{verticalAlign: 'top'}}>
                        {
                            summary.compare && (
                                <div>
                                    <Typography.Title level={2} style={{fontSize: '11pt', fontFamily: fontFamily}}>
                                        {planTitle?.compare || 'Comparison' }{' '}Plan
                                    </Typography.Title>
                                    {(ResultCellHighlight(summary.base?.length, summary.compare?.length))} feet ({(summary.compare?.length/5280).toFixed(2)} miles) reach
                                    <Plural
                                        count={summary.compare?.crossSections}
                                        singular={'cross section'}
                                        plural={'cross sections'}
                                        render={(count, text) => (
                                            <>
                                                <br/>
                                                {ResultCellHighlight(summary.base?.crossSections, count)} {text}
                                            </>
                                        )}
                                    />
                                    <Plural
                                        count={summary.compare.single_bridge}
                                        singular={'(single opening) bridge'}
                                        plural={'(single opening) bridges'}
                                        render={(count, text) => {
                                            return (
                                                <>
                                                    <br/>
                                                    {ResultCellHighlight(summary.base?.single_bridge, count)} {text}
                                                </>
                                            )
                                        }}
                                    />
                                    <Plural
                                        count={summary.compare.multiple_bridge}
                                        singular={'(multiple opening) bridge'}
                                        plural={'(multiple opening) bridges'}
                                        render={(count, text) => {
                                            return (
                                                <>
                                                    <br/>
                                                    {ResultCellHighlight(summary.base?.multiple_bridge, count)} {text}
                                                </>
                                            )
                                        }}
                                    />
                                    <Plural
                                        count={summary.compare?.culverts}
                                        singular={'culvert'}
                                        plural={'culverts'}
                                        render={(count, text) => {
                                            return (
                                                <>
                                                    <br/>
                                                    {ResultCellHighlight(summary.base?.culverts, count)} {text}
                                                </>
                                            )
                                        }}
                                    />
                                    <Plural
                                        count={summary.compare?.inline}
                                        singular={'inline structure'}
                                        plural={'inline structures'}
                                        render={(count, text) => {
                                            return (
                                                <>
                                                    <br/>
                                                    {ResultCellHighlight(summary.compare?.inline, count)} {text}
                                                </>
                                            )
                                        }}
                                    />
                                    <Plural
                                        count={summary.compare?.profiles}
                                        singular={'profile'}
                                        plural={'profiles'}
                                        render={(count, text) => (
                                            <>
                                                <br/>
                                                {ResultCellHighlight(summary.base?.profiles, count)} {text}
                                                <Plural
                                                    count={summary.base?.flowLocations}
                                                    singular={'location'}
                                                    plural={'locations'}
                                                    render={(count, text) => (
                                                        <>
                                                            {' '} with {ResultCellHighlight(summary.base?.flowLocations, count)} flow change {text}
                                                        </>
                                                    )}
                                                />
                                            </>
                                        )}
                                    />
                                </div>
                            )
                        }
                    </td>
                </tr>
            </table>
        </div>
    )
}

const renderWSEL = (wsel, filter, fontFamily, fontSize,) => {

    let hasLimit = false
    if(
        parseFloat(wsel.revised_reach_min) === parseFloat(wsel.minCS)
        || parseFloat(wsel.revised_reach_max) === parseFloat(wsel.maxCS)
        || parseFloat(wsel.affected_reach_min) === parseFloat(wsel.minCS)
        || parseFloat(wsel.affected_reach_max) === parseFloat(wsel.maxCS)
        || parseFloat(wsel.project_min) === parseFloat(wsel.minCS)
        || parseFloat(wsel.project_max) === parseFloat(wsel.maxCS)
        || parseFloat(filter?.ds) === parseFloat(wsel.minCS)
        || parseFloat(filter?.us) === parseFloat(wsel.maxCS)
    ) {
        hasLimit = true
    }

    const toLimitStr = (cs) => {
        if(parseFloat(cs) === wsel.minCS || parseFloat(cs) === wsel.maxCS)
            return (
                <>
                    {parseFloat(cs)}<sup>(1)</sup>
                </>
            )

        return cs
    }

    const renderRevised = () => {
        if(wsel.revised_reach_min === -1 && wsel.revised_reach_max === -1)
            return (<div>Geometry is identical!</div>)

        if(wsel.revised_reach_min === 0 && wsel.revised_reach_max === 0)
            return (<div>[DS - US]</div>)

        if(wsel.revised_reach_min > 0 || wsel.revised_reach_max > 0)
        {
            return (<div>[{toLimitStr(wsel.revised_reach_min)} - {toLimitStr(wsel.revised_reach_max)}]</div>)
        }
    }

    const renderAffected = () => {
        if(wsel.affected_reach_min === -1 && wsel.affected_reach_max === -1)
            return (<div>100yr WSEL profile is identical!</div>)

        if(wsel.affected_reach_min === 0 && wsel.affected_reach_max === 0)
            return (<div>[DS - US]</div>)

        return (<div>[{toLimitStr(wsel.affected_reach_min)} - {toLimitStr(wsel.affected_reach_max)}]</div>)
    }

    const renderProjectArea = () => {
        if(
            wsel.project_min === -1
            && wsel.project_max === -1
        )
            return 'Two plans are identical!'

        if(
            wsel.project_min === 0
            && wsel.project_max === 0
        )
            return '[DS - US]'

        return (
            <>
                <div>
                    [{ResultCellHighlight(toLimitStr(filter?.ds || wsel.project_min), toLimitStr(wsel.project_min))}
                    {' '}-{' '}
                    {ResultCellHighlight(toLimitStr(filter?.us || wsel.project_max), toLimitStr(wsel.project_max))}]
                </div>
            </>
        )
    }


    let topics = _.groupBy(wsel?.topics?.filter(x => parseFloat(x.name) < parseFloat(filter.ds) || parseFloat(x.name) > parseFloat(filter.us)), function(b) { return b.name})


    // console.log(wsel)
    return (
        <>
            <table style={{fontFamily: fontFamily, fontSize: fontSize}}>
                <tr>
                    <td style={{fontWeight: 'bold', width: 200}}>
                        Revised (Geometry) Reach:
                    </td>
                    <td>
                        {renderRevised()}
                    </td>
                </tr>
                <tr>
                    <td style={{fontWeight: 'bold'}}>
                        Affected Reach:
                    </td>
                    <td>
                        {renderAffected()}
                    </td>
                </tr>

                <tr>
                    <td style={{fontWeight: 'bold'}}>
                        Identified Project Reach:
                    </td>
                    <td>
                        <div>
                            {renderProjectArea()}
                        </div>
                    </td>
                    <td style={{width: 50}}>

                    </td>
                    <td style={{fontWeight: 'bold', width: 115}}>
                        Bounding XS:
                    </td>
                    <td>
                        {
                            wsel.boundary_min > -1 ? (
                                <text>
                                    ({toLimitStr(wsel.boundary_min)} - {toLimitStr(wsel.boundary_max)})
                                </text>
                            ) : (<text>None</text>)
                        }
                    </td>
                </tr>
                {
                    filter && (filter.us || filter.ds) && (
                        <>
                            <tr>
                                <td colSpan={4}></td>
                            </tr>
                            <tr>
                                <td style={{fontWeight: 'bold'}}>
                                    Selected Project Reach:
                                </td>
                                <td>
                                    [{toLimitStr(filter?.ds)} - {toLimitStr(filter?.us)}]
                                </td>
                            </tr>
                        </>
                    )
                }
            </table>
            <EmptyLine />
            {
                hasLimit && (
                    <div style={{fontSize: fontSize, fontFamily: fontFamily}}>
                        <sup>(1)</sup> US/DS limit of the model.
                    </div>
                )
            }
            <EmptyLine />
            <ResultText>
                {
                    (toLimitStr(filter?.ds) !== toLimitStr(wsel.project_min) || toLimitStr(filter?.us) !== toLimitStr(wsel.project_max)) && Object.keys(topics).length > 0 && (
                        <>
                            <table style={{fontFamily: fontFamily, fontSize: fontSize}}>
                                <tbody>
                                <tr>
                                    <td style={{verticalAlign: 'top', width: 50, color: 'red'}}>
                                        PRJ-00
                                    </td>
                                    <td>
                                        <text style={{color: 'red'}}>
                                            Identified Project Reach is different than the Selected Project Reach at the following {topics?.length} cross sections!
                                        </text>
                                        <EmptyLine />
                                        {Object.keys(topics).map(key => {
                                            let val = new Set(topics[key].map(x => x.title))
                                            val = Array.from(val)
                                            return `${key} (${val.join(', ')})`
                                        }).join(', ')}
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </>
                    )
                }
            </ResultText>
        </>
    )
}